import { FormEvent, MouseEventHandler, RefObject, useEffect, useReducer } from 'react';

import { Variant as BannerVariant } from '@leafygreen-ui/banner';

import { FieldNames } from '@packages/types/auth/field';
import { Search } from '@packages/types/auth/search';
import { Document } from '@packages/types/browser';

import * as api from 'js/common/services/api';
import * as errorHelper from 'js/common/services/authErrorHelper';
import { useRequestParams } from 'js/common/context/RequestParamsContext';
import { justLoggedInRedirect } from 'js/common/utils/authRedirectUtils';

import { ReCaptchaHandles } from 'js/auth/components/ReCaptcha';
// reducers
import { loginReducer, reducerState, ReducerState } from 'js/auth/reducers/loginReducer';
import { onChangeTypeDef } from 'js/auth/types/formInput';

interface AuthResponse {
  uId?: string;
  username?: string;
  email?: string;
  currentOrgId?: string;
  csrfTime?: string;
  csrfToken?: string;
  loginRedirect: string;
  status: string;
}

export const ALERTABLE_ERROR_CODES = [
  'SOCIAL_USERNAME_DOMAIN_REQUIRES_FEDERATION',
  'USER_ORGS_RESTRICTED',
  'SSO_DEBUG_PROHIBITED',
  'IDLE_SESSION_TIMEOUT',
  'ACCOUNT_DELETION_REQUESTED',
  'JIT_FAILURE',
];

export interface UseLoginService {
  onLoginUser: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  onEmailChange: onChangeTypeDef;
  onPasswordChange: onChangeTypeDef;
  onChangeEmailClick: MouseEventHandler<HTMLAnchorElement>;
  disableEmailInput: boolean;
  showInputErrors: boolean;
  loginReducerState: ReducerState;
  onSetReCaptchaResponse: (string) => void;
}

export default function useLoginService({
  parsedSearchLocation,
  isVercelIntegration = false,
  windowLocation,
  reCaptchaRef,
  browserDocument,
  clientState = { ...parsedSearchLocation },
}: {
  parsedSearchLocation: Search;
  isVercelIntegration?: boolean;
  windowLocation: Pick<Location, 'assign'>;
  reCaptchaRef: RefObject<ReCaptchaHandles>;
  browserDocument: Document;
  clientState?: object;
}): UseLoginService {
  const { onPrem, userRegistrationDisabledRedirectUrl, vercelEmail, reCaptchaEnabledLogin, baasCentralUrl } =
    useRequestParams();

  const {
    inviteToken,
    username: invitedUsername,
    socialUsername,
    unauthorizedUsername,
    reason,
    ssoDebug,
    idp,
  } = parsedSearchLocation;

  const [loginReducerState, dispatch] = useReducer(loginReducer, reducerState, (state) => {
    return {
      ...state,
      onlyUsernameShowing: !onPrem,
    };
  });

  const {
    username,
    password,
    onlyUsernameShowing,
    // showSupportLogout,
    authErrorCode,
    reCaptchaResponse,
    differentUniversityPasswordError,
    ssoBypassEnabled,
    bannerVariant,
  } = loginReducerState;

  const onCallAuthApi = async () => {
    const loginData = {
      username,
      password,
      inviteToken,
      clientState,
      reCaptchaResponse,
      ssoDebug,
      idp,
    };

    let authResponse: AuthResponse;

    try {
      authResponse = await api.user.auth(loginData);
    } catch ({ errorCode, resource }) {
      dispatch({ type: 'processAuthErrorResponse', payload: { errorCode, formSubmitting: false } });

      if (reCaptchaEnabledLogin && reCaptchaRef.current) {
        reCaptchaRef.current.reset();
      }

      if (errorHelper.hasMatchingErrorCode(errorCode) && resource) {
        windowLocation.assign(`${resource}?reason=${errorCode}`);
      }

      return;
    }

    const { uId: userId, username: responseUsername, email } = authResponse;

    justLoggedInRedirect({
      userId,
      email,
      baasCentralUrl,
      username: responseUsername,
      windowLocation,
      query: parsedSearchLocation,
    });
  };

  const prefilledUsername = invitedUsername || socialUsername || unauthorizedUsername;

  useEffect(() => {
    if (prefilledUsername) {
      dispatch({ type: 'field', payload: { field: FieldNames.USERNAME, value: prefilledUsername } });
    }
  }, [prefilledUsername, isVercelIntegration, vercelEmail]);

  useEffect(() => {
    if (reason) {
      const reasonBannerVariant =
        reason === 'SOCIAL_USERNAME_DOMAIN_REQUIRES_FEDERATION'
          ? BannerVariant.Warning
          : reason === 'IDLE_SESSION_TIMEOUT' || reason === 'ACCOUNT_DELETION_REQUESTED'
          ? BannerVariant.Info
          : BannerVariant.Danger;
      dispatch({
        type: 'processAuthErrorResponse',
        payload: {
          errorCode: reason,
          bannerVariant: reasonBannerVariant,
          formSubmitting: false,
          marketingUrl: userRegistrationDisabledRedirectUrl,
        },
      });
    }
  }, [reason]);

  useEffect(() => {
    let disableSubmit = true;
    if ((onlyUsernameShowing && username) || password) {
      disableSubmit = false;
    }
    dispatch({ type: 'field', payload: { field: FieldNames.IS_SUBMIT_DISABLED, value: disableSubmit } });
  }, [password, username, onlyUsernameShowing]);

  useEffect(() => {
    if (reCaptchaResponse != null) {
      onCallAuthApi();
    }
  }, [reCaptchaResponse]);

  useEffect(() => {
    if (differentUniversityPasswordError) {
      windowLocation.assign(`/account/reset/university/password?email=${encodeURIComponent(username)}`);
    }
  }, [differentUniversityPasswordError]);

  useEffect(() => {
    if (ssoDebug) {
      dispatch({ type: 'bypassSso' });
    }
  }, [ssoDebug]);

  const onLoginUser = async (e) => {
    e.preventDefault();
    dispatch({ type: 'setFormSubmitting', payload: true });
    const hasValidRememberDeviceCookie =
      browserDocument.cookie && browserDocument.cookie.includes(`remember-user-device=${username.toLowerCase()}:`);

    if (onlyUsernameShowing) {
      try {
        const { loginRedirect } = await api.federation.getIdentityProviderRedirect({
          username: username!,
          clientState,
        });

        if (loginRedirect) {
          windowLocation.assign(loginRedirect);
        }

        dispatch({
          type: 'processIdpResponse',
          payload: {
            onlyUsernameShowing: !onPrem && !!loginRedirect,
            errorCode: '',
            isSubmitDisabled: false,
          },
        });
      } catch (err) {
        const { errorCode } = err;
        dispatch({
          type: 'processIdpResponse',
          payload: {
            onlyUsernameShowing: true,
            errorCode,
            isSubmitDisabled: false,
          },
        });
      } finally {
        dispatch({ type: 'setFormSubmitting', payload: false });
      }
    } else if (reCaptchaEnabledLogin && reCaptchaRef.current && !hasValidRememberDeviceCookie) {
      reCaptchaRef.current.execute();
    } else {
      onCallAuthApi();
    }
  };

  const onEmailChange: onChangeTypeDef = (e) => {
    dispatch({ type: 'field', payload: { field: FieldNames.USERNAME, value: e.target.value } });
  };

  const onPasswordChange: onChangeTypeDef = (e) => {
    dispatch({ type: 'field', payload: { field: FieldNames.PASSWORD, value: e.target.value } });
  };

  const onChangeEmailClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    dispatch({ type: 'changeUsername' });
  };

  const onSetReCaptchaResponse = (response) => {
    dispatch({ type: 'setReCaptchaResponse', payload: { reCaptchaResponse: response } });
  };

  const disableEmailInput = !onlyUsernameShowing && !ssoBypassEnabled && !onPrem;

  const prefilledUsernameChanged = prefilledUsername && username !== prefilledUsername;

  const showInputErrors =
    !!authErrorCode &&
    !ALERTABLE_ERROR_CODES.includes(authErrorCode) &&
    bannerVariant === BannerVariant.Danger &&
    !prefilledUsernameChanged;

  return {
    onLoginUser,
    onEmailChange,
    onPasswordChange,
    onChangeEmailClick,
    disableEmailInput,
    showInputErrors,
    loginReducerState,
    onSetReCaptchaResponse,
  };
}
